// tradeLineage.js
// Where an item came from, and what it turned into.
//
// WHY THIS EXISTS
// A traded-away row is no longer a holding — the Live tabs and every portfolio
// total drop it (see isTradedAway in helpers.js). That is correct, but on its
// own it would mean an item you owned for months simply stops being anywhere
// you would think to look for it. So the record has to stay joined up: from any
// card in the app you can now walk backwards to the item that paid for it and
// forwards to whatever it became, however many trades ago that was.
//
// THE SHAPE OF THE DATA
// A trade writes two things (SkinTable.handleTradeItem, and autoLinkTrades on
// the server, which writes the identical shape):
//
//   offered row    tradeId: 'trade_xxx'  tradedFor: [{name, condition}, ...]
//   received rows  receivedFromTradeId: 'trade_xxx'   buyPrice: its slice of
//                                                     the offered row's basis
//
// One tradeId per item given away — a trade where you hand over two knives
// writes two tradeIds — so a tradeId names exactly one offered row and the
// links form a tree, not a graph.
//
// A row can hold BOTH fields at once: received in one trade, given away in a
// later one. That is what makes this a chain rather than a single hop, and it
// is the whole point — three trades later, "what did this actually cost me"
// only has an answer if you can walk back to the item that started it.

import { isTradedAway } from './helpers';
import { normalizeTradePartner } from './TradePartner';

/** Hard stop on the walk. Corrupt links can't spin this forever. */
const MAX_DEPTH = 12;

/**
 * Index every trade link in one pass.
 *
 * Built once per row list and handed to buildLineage, because a detail modal
 * opened on a card would otherwise rescan the whole tracker per hop.
 */
export function buildTradeIndex(rows) {
  const gave = new Map();   // tradeId -> [{ row, index }] everything handed over
  const got  = new Map();   // tradeId -> [{ row, index }] everything that came back

  (rows || []).forEach((row, index) => {
    if (!row) return;
    // A LIST, not one row. One Steam trade can hand over seven items for
    // thirty-seven, and they all share a tradeId — treating the left side as a
    // single row is what used to draw that trade as seven separate ones.
    if (row.tradeId) {
      if (!gave.has(row.tradeId)) gave.set(row.tradeId, []);
      gave.get(row.tradeId).push({ row, index });
    }
    const from = row.receivedFromTradeId;
    if (from) {
      if (!got.has(from)) got.set(from, []);
      got.get(from).push({ row, index });
    }
  });

  return { gave, got };
}

/** Cash on the side of a trade, signed: + you took it, - you added it. */
function bootOf(row) {
  const amount = Number(row?.boot?.amount);
  return Number.isFinite(amount) ? amount : 0;
}

/**
 * Everything one hop of the chain needs to be drawn.
 *
 * `gave` is a LIST: one trade can hand over seven items for thirty-seven, and
 * every one of them carries the same tradeId. The date, the counterparty and
 * the cash belong to the trade rather than to any single item, so they are read
 * off the first — identical across the whole side by construction.
 *
 * An empty list is a real state, not an error: rows imported before
 * auto-linking existed name a trade that nothing in the tracker answers to.
 */
function hopOf(tradeId, gaveList) {
  const first = gaveList?.[0]?.row || null;
  return {
    tradeId,
    // tradedAt, then timestamp. They are different dates and only the first is
    // the trade's: `timestamp` is when the row was acquired, so an item you
    // held for six weeks before trading it away dates its trade to the day you
    // bought it. Rows written before tradedAt existed still fall back, which is
    // right for the common case where the linker created the row at trade time.
    at: first?.tradedAt || first?.timestamp || null,
    partner: normalizeTradePartner(first?.tradedWith),
    boot: bootOf(first),
    gave: gaveList || [],
  };
}

/**
 * Walk backwards: the trades that led to this item being yours.
 *
 * Returned oldest first, so rendering it in order reads as a story — the
 * original purchase, then each swap, then the item in your hand.
 */
function walkBack(index, row) {
  const out = [];
  const seen = new Set();
  let cur = row;

  for (let d = 0; d < MAX_DEPTH; d++) {
    const from = cur?.receivedFromTradeId;
    if (!from || seen.has(from)) break;
    seen.add(from);

    const gaveList = index.gave.get(from) || [];
    const hop = hopOf(from, gaveList);
    // EVERYTHING that came back in this trade, the row we are walking from
    // included. It used to be filtered out, which was wrong for the overview:
    // "which of these five is the one I clicked" is half of what the panel is
    // being asked, and it can only be answered by drawing it in place and
    // marking it. They shared this row's cost basis in any case, so they all
    // belong to the answer to "what did this cost me".
    hop.received = (index.got.get(from) || []).map(e => ({ row: e.row, index: e.index }));
    out.push(hop);

    // The chain can branch backwards when several items paid for this one. The
    // walk follows the first; the rest stay visible on the hop itself.
    if (!gaveList.length) break;   // chain runs off the end of what we know
    cur = gaveList[0].row;
  }

  return out.reverse();
}

/**
 * Walk forwards: what this item became, and what those became.
 *
 * A tree, because one item can come back as five. Each node carries whether it
 * was sold (the chain ends in money) or traded on again (the chain continues).
 */
function walkForward(index, row, depth, seen) {
  if (depth >= MAX_DEPTH) return null;
  const tradeId = row?.tradeId;
  if (!tradeId || !isTradedAway(row) || seen.has(tradeId)) return null;
  seen.add(tradeId);

  const received = (index.got.get(tradeId) || []).map(e => ({
    row: e.row,
    index: e.index,
    sold: parseFloat(e.row?.sellPrice) > 0,
    // Recurse: an item received here and traded away later keeps the chain
    // going, and that is exactly the case a single-hop view got wrong.
    next: walkForward(index, e.row, depth + 1, seen),
  }));

  return {
    // Walking forwards, the row we are standing on is the side that went out.
    // Anything else given in the same trade is added by buildLineage, which can
    // see the whole row list.
    ...hopOf(tradeId, [{ row, index: -1 }]),
    // tradedFor is the record of what was promised, which survives even when no
    // received rows were ever created. Named separately so an empty `received`
    // with a non-empty `promised` reads as "logged but never materialised"
    // rather than as an empty trade.
    promised: Array.isArray(row.tradedFor) ? row.tradedFor : [],
    received,
  };
}

/** Flatten a forward tree into a count of items and how many are still held. */
function tally(node, acc = { items: 0, sold: 0, onward: 0 }) {
  if (!node) return acc;
  for (const r of node.received) {
    acc.items++;
    if (r.sold) acc.sold++;
    if (r.next) { acc.onward++; tally(r.next, acc); }
  }
  return acc;
}

/**
 * The full chain around one row.
 *
 * @param rows  every tracker row, in array order
 * @param row   the row to centre the chain on
 * @param index optional prebuilt buildTradeIndex result
 * @returns {{ origin, exit, totals, has }} — `has` is false when this row has
 *          never been on either side of a trade, which is the common case and
 *          the signal for the caller to render nothing at all.
 */
export function buildLineage(rows, row, index = null) {
  if (!row) return { origin: [], exit: null, totals: null, has: false };
  const idx = index || buildTradeIndex(rows);

  const origin = walkBack(idx, row);
  const exit   = walkForward(idx, row, 0, new Set());

  // walkForward only knows the row it started from, but a trade can have had
  // several items on its left side. Fill the rest in here, where the whole row
  // list is in scope, so the panel shows the trade rather than one leg of it.
  if (exit) {
    const whole = idx.gave.get(exit.tradeId);
    if (whole?.length) exit.gave = whole;
  }

  return {
    origin,
    exit,
    totals: exit ? tally(exit) : null,
    has: origin.length > 0 || !!exit,
  };
}

/**
 * Every trade in the row list, assembled as one thing each.
 *
 * WHY THE TABS CANNOT JUST FILTER
 * The Traded views used to list offered rows and treat each as a trade. That
 * was only ever right because the server minted a tradeId per given item — one
 * Steam trade became five cards. Now a trade has one id and several rows carry
 * it, so listing offered rows would draw the same trade five times, each
 * showing the same received side.
 *
 * Grouping is the whole fix, and doing it here means the three Traded views and
 * the detail modal all reassemble trades the same way.
 *
 * Sold offered rows are excluded, matching what the views already did: a row
 * that was traded and later sold belongs to the sale.
 *
 * @returns [{ tradeId, gave, received, at, partner, boot, promised }] newest first
 */
export function buildTrades(rows, index = null) {
  const idx = index || buildTradeIndex(rows);
  const out = [];

  for (const [tradeId, gaveList] of idx.gave) {
    const gave = gaveList.filter(e => e.row?.skin?.value && !(parseFloat(e.row.sellPrice) > 0));
    if (!gave.length) continue;

    const hop = hopOf(tradeId, gave);
    hop.received = (idx.got.get(tradeId) || []).map(e => ({ row: e.row, index: e.index }));
    // What the trade said came back, which survives even when no rows were ever
    // created for it — the difference between "nothing came back" and "nothing
    // was recorded". Taken from the first leg; every leg carries the same list.
    hop.promised = Array.isArray(gave[0].row.tradedFor) ? gave[0].row.tradedFor : [];
    out.push(hop);
  }

  return out.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
}

/**
 * The cost basis this row inherited, and from what.
 *
 * A received row's buyPrice is a slice of the offered item's, so "you paid
 * $124" is true of the row and misleading on its own. This names the item the
 * money actually came from, at the far end of however many hops.
 */
export function basisOrigin(lineage) {
  const first = lineage?.origin?.[0];
  const gave = first?.gave || [];
  if (!gave.length) return null;
  return {
    // Every item that paid for this one, not just the first — a trade with
    // seven items on the left has seven answers to "what did this cost me".
    rows: gave.map(e => e.row),
    row: gave[0].row,
    index: gave[0].index,
    at: first.at,
    hops: lineage.origin.length,
  };
}
